import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, MapPin, Home, Clock, Flame, CalendarCheck, FileText } from "lucide-react";
import { LeadCard } from "./HeroConsole";
import { DEMO_LEADS } from "../../lib/demoLeads";

const useTick = (ms, n) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((v) => (v + 1) % n), ms);
    return () => clearInterval(id);
  }, [ms, n]);
  return t;
};

const DOTS = Array.from({ length: 60 }, (_, i) => i);
const HOT = [7, 14, 22, 31, 38, 45, 52];

const V1 = () => {
  const t = useTick(700, HOT.length);
  return (
    <div className="h-full flex flex-col">
      <p className="font-mono text-[11px] text-slate-400 mb-4">Persone interessate intercettate nell'area</p>
      <div className="grid grid-cols-10 gap-2.5 sm:gap-3 flex-1 content-center">
        {DOTS.map((d) => {
          const k = HOT.indexOf(d);
          const on = k > -1 && k <= t;
          return <span key={d} className={`aspect-square rounded-full transition-[background-color,box-shadow] duration-500 ${on ? "bg-[#ff5500] shadow-[0_0_14px_#ff5500]" : "bg-white/10"}`} />;
        })}
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        {["Pompa di calore", "Fotovoltaico", "Climatizzatore"].map((s) => (
          <span key={s} className="text-[11px] px-2 py-1 rounded-md border hairline text-slate-300">{s}</span>
        ))}
      </div>
    </div>
  );
};

const CHECKS = [
  { l: "Tipo di impianto", v: "Pompa di calore", I: Flame },
  { l: "Tipologia immobile", v: "Villetta indipendente", I: Home },
  { l: "Zona", v: "Napoli", I: MapPin },
  { l: "Tempistica", v: "Entro 3 mesi", I: Clock },
];

const V2 = () => {
  const t = useTick(900, CHECKS.length + 2);
  return (
    <div className="h-full flex flex-col">
      <p className="font-mono text-[11px] text-slate-400 mb-4">Qualificazione della richiesta</p>
      <ul className="space-y-2.5">
        {CHECKS.map(({ l, v, I }, i) => {
          const ok = i < t;
          return (
            <li key={l} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-colors duration-500 ${ok ? "border-[#4fc3f7]/30 bg-[#4fc3f7]/5" : "hairline bg-white/[0.02]"}`}>
              <I className="w-4 h-4 text-slate-400" />
              <span className="text-[13px] text-slate-300 flex-1">{l}</span>
              <span className={`text-[13px] transition-opacity duration-500 ${ok ? "opacity-100 text-white" : "opacity-0"}`}>{v}</span>
              <span className={`w-5 h-5 grid place-items-center rounded-full transition-colors ${ok ? "bg-[#4fc3f7] text-[#06101e]" : "bg-white/5"}`}>{ok && <Check className="w-3 h-3" />}</span>
            </li>
          );
        })}
      </ul>
      <div className="mt-5">
        <div className="flex justify-between text-[11px] font-mono text-slate-400 mb-1.5"><span>Profilazione</span><span>{Math.min(t, 4) * 25}%</span></div>
        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#4fc3f7] to-[#ff5500] transition-[width] duration-700" style={{ width: `${Math.min(t, 4) * 25}%` }} />
        </div>
      </div>
    </div>
  );
};

const V3 = () => {
  const t = useTick(2600, 3);
  return (
    <div className="h-full flex flex-col">
      <p className="font-mono text-[11px] text-slate-400 mb-4">Appuntamento fissato da TERMONLINE</p>
      <div className="relative flex-1 min-h-[220px]">
        <motion.div key={t} initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <LeadCard lead={DEMO_LEADS[t]} compact />
        </motion.div>
        <div className="absolute bottom-0 inset-x-0 flex items-center gap-3 rounded-xl border border-dashed border-[#ff5500]/40 px-4 py-3">
          <CalendarCheck className="w-5 h-5 text-[#ff8a4c]" />
          <span className="text-[13px] text-white">Il tuo calendario</span>
          <span className="ml-auto font-mono text-[11px] text-[#ffb07f]">Gio · 10:30</span>
        </div>
      </div>
    </div>
  );
};

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven"];
const V4 = () => {
  const t = useTick(1400, 4);
  return (
    <div className="h-full flex flex-col">
      <p className="font-mono text-[11px] text-slate-400 mb-4">La tua azienda va all'appuntamento</p>
      <div className="grid grid-cols-5 gap-2">
        {DAYS.map((d, i) => (
          <div key={d} className="rounded-xl border hairline p-2 min-h-[120px] bg-white/[0.02]">
            <p className="font-mono text-[10px] text-slate-400 mb-2">{d}</p>
            {i === 3 && t >= 1 && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="rounded-lg bg-[#ff5500] text-white text-[10px] sm:text-[11px] p-1.5 leading-tight">
                <CalendarCheck className="w-3 h-3 mb-0.5" />Sopralluogo 10:30
              </motion.div>
            )}
            {i === 1 && <div className="rounded-lg bg-white/5 h-6" />}
          </div>
        ))}
      </div>
      <div className={`mt-4 flex items-center gap-3 rounded-xl px-4 py-3 border transition-colors duration-500 ${t >= 2 ? "border-[#4fc3f7]/40 bg-[#4fc3f7]/5" : "hairline"}`}>
        <FileText className="w-4 h-4 text-[#7fd6fb]" />
        <span className="text-[13px] text-white">Preventivo</span>
        <span className="ml-auto font-mono text-[11px] text-slate-300">{t >= 3 ? "inviato" : t >= 2 ? "in preparazione" : "in attesa"}</span>
      </div>
    </div>
  );
};

export const PROCESS_VISUALS = [V1, V2, V3, V4];
